import { useLocation } from 'react-router-dom';
import { IS_AI_AND_BRAND_KIT_ENABLED } from 'admin/constants/release-features';
import { useEffect } from 'react';
import { setStoryEditorUI } from 'admin/actions/story/set-story-editor-ui';
import { MODAL } from 'admin/constants/common';
import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import { selectStoryEditorFocusMode, selectStoryEditorInspectorOpen } from 'admin/reducers/story-editor/selectors';
import { getClientFrame } from 'common/utils/iframe-tunnel';
import { isCMD } from 'utils/helpers';
import { resolveEditorSurface } from './StoryEditorChat/editor-access';
import { useStoryEditorChatState } from './StoryEditorChat/hooks/use-story-editor-chat-state';
import { EditorKeyboardEvEm, STORY_EDITOR_IFRAME_KEYDOWN_EVENT } from './story-editor-keyboard-events';

export type StoryEditorShortcut =
	| 'toggleAddComponent'
	| 'toggleChat'
	| 'toggleFocusMode'
	| 'toggleInspector'
	| 'toggleLayers'
	| 'togglePreview';

type StoryEditorShortcutInput = {
	alt?: boolean;
	code: string;
	command: boolean;
	shift?: boolean;
};

export const resolveStoryEditorShortcut = ({
	alt = false,
	code,
	command,
	shift = false,
}: StoryEditorShortcutInput): StoryEditorShortcut | null => {
	if (command && code === 'Backslash') return 'toggleFocusMode';
	if (command && code === 'Slash') return 'toggleChat';
	if (!command && !alt && !shift) {
		if (code === 'KeyA') return 'toggleAddComponent';
		if (code === 'KeyI') return 'toggleInspector';
		if (code === 'KeyL') return 'toggleLayers';
		if (code === 'KeyP') return 'togglePreview';
	}
	return null;
};

const CARD_ONLY_SHORTCUTS = new Set<StoryEditorShortcut>([
	'toggleAddComponent',
	'toggleFocusMode',
	'toggleInspector',
	'toggleLayers',
]);
const EDITABLE_BLOCKED_SHORTCUTS = new Set<StoryEditorShortcut>([
	'toggleAddComponent',
	'toggleInspector',
	'toggleLayers',
	'togglePreview',
]);

const isEditableTarget = (event: KeyboardEvent) => {
	const target = event.target as HTMLElement | null;
	if (!target?.nodeName) return false;
	if (target.isContentEditable || target.contentEditable === 'true') return true;
	return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.nodeName);
};

type UseStoryEditorShortcutsParams = {
	canToggleInspector: boolean;
	isChatOpen: boolean;
	onChatOpenChange: (open: boolean) => void;
};

export const useStoryEditorShortcuts = ({
	canToggleInspector,
	isChatOpen,
	onChatOpenChange,
}: UseStoryEditorShortcutsParams) => {
	const dispatch = useAdminDispatch();
	const focusMode = useAdminSelector(selectStoryEditorFocusMode);
	const inspectorOpen = useAdminSelector(selectStoryEditorInspectorOpen);
	const isModalOpen = useAdminSelector(state => state.modal.id !== MODAL.NONE);

	useEffect(() => {
		const handledEvents = new WeakSet<KeyboardEvent>();
		const onKeyDown = (event: KeyboardEvent) => {
			if (handledEvents.has(event) || event.defaultPrevented || event.repeat || isModalOpen) return;
			handledEvents.add(event);

			const shortcut = resolveStoryEditorShortcut({
				alt: event.altKey,
				code: event.code,
				command: isCMD(event),
				shift: event.shiftKey,
			});
			if (
				!shortcut ||
				(shortcut === 'toggleChat' && !IS_AI_AND_BRAND_KIT_ENABLED) ||
				(CARD_ONLY_SHORTCUTS.has(shortcut) && !canToggleInspector) ||
				(EDITABLE_BLOCKED_SHORTCUTS.has(shortcut) && isEditableTarget(event))
			) {
				return;
			}

			event.preventDefault();
			switch (shortcut) {
				case 'toggleAddComponent':
					EditorKeyboardEvEm.emit('toggleAddComponent');
					break;
				case 'toggleChat':
					onChatOpenChange(!isChatOpen);
					break;
				case 'toggleFocusMode':
					dispatch(setStoryEditorUI({ focusMode: !focusMode }));
					break;
				case 'toggleInspector':
					dispatch(setStoryEditorUI({ inspectorOpen: !inspectorOpen }));
					break;
				case 'toggleLayers':
					EditorKeyboardEvEm.emit('toggleLayers');
					break;
				case 'togglePreview':
					EditorKeyboardEvEm.emit('togglePreview');
					break;
				default:
					break;
			}
		};
		const onIframeKeyDown = (event: Event) => {
			onKeyDown((event as CustomEvent<KeyboardEvent>).detail);
		};
		const frameDocument = canToggleInspector ? getClientFrame().contentDocument : null;

		document.addEventListener('keydown', onKeyDown);
		frameDocument?.addEventListener('keydown', onKeyDown);
		window.addEventListener(STORY_EDITOR_IFRAME_KEYDOWN_EVENT, onIframeKeyDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			frameDocument?.removeEventListener('keydown', onKeyDown);
			window.removeEventListener(STORY_EDITOR_IFRAME_KEYDOWN_EVENT, onIframeKeyDown);
		};
	}, [canToggleInspector, dispatch, focusMode, inspectorOpen, isChatOpen, isModalOpen, onChatOpenChange]);
};

/** Editor shortcuts remain available when the chat is not mounted. */
export function StoryEditorShortcuts() {
	const { pathname } = useLocation();
	const { isOpen, onOpenChange } = useStoryEditorChatState();
	useStoryEditorShortcuts({
		canToggleInspector: resolveEditorSurface(pathname).editorSurface === 'card',
		isChatOpen: isOpen,
		onChatOpenChange: onOpenChange,
	});
	return null;
}
